import {
  CAMPAIGN_END_DATE_TOKEN,
  COPY_FEEDBACK_DURATION_MS,
  dedupeOffers,
  formatCampaignEnd,
  normalizeOfferCode,
  offerIsVisible,
  safeOfferUrl,
  sortOffers,
  validatePublicOfferSnapshot,
} from './my-offers-domain.js?v=2026081801';

const controllers = new WeakMap();
const PUBLIC_TIMEOUT_MS = 8_000;
const MAX_VISIBLE_OFFERS = 2;

function element(tagName, className, text) {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text !== undefined && text !== null) node.textContent = String(text);
  return node;
}

function button(className, text) {
  const node = element('button', className, text);
  node.type = 'button';
  return node;
}

function legacyCopy(value) {
  const field = element('textarea');
  field.value = value;
  field.readOnly = true;
  field.style.position = 'fixed';
  field.style.opacity = '0';
  field.style.pointerEvents = 'none';
  document.body.append(field);
  field.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }
  field.remove();
  return copied;
}

async function writeClipboard(value) {
  if (typeof navigator.clipboard?.writeText === 'function') {
    await navigator.clipboard.writeText(value);
    return true;
  }
  return legacyCopy(value);
}

function parseNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

class MyOffersController {
  constructor(root) {
    this.root = root;
    this.config = this.readConfig();
    this.text = this.config.translations || {};
    this.publicRequest = null;
    this.copyTimers = new Map();
    this.destroyed = false;
    this.handleClick = this.handleClick.bind(this);
    this.handleMediaError = this.handleMediaError.bind(this);
  }

  readConfig() {
    const source = this.root.querySelector('[data-my-offers-config]')?.textContent || '{}';
    try {
      return JSON.parse(source);
    } catch {
      return {};
    }
  }

  init() {
    this.root.addEventListener('click', this.handleClick);
    this.root.addEventListener('error', this.handleMediaError, true);
    this.hydrateCampaignEnd();
    this.hydrateCampaignActions();
    this.sortVisibleCards();
    this.loadPublicOffers();
  }

  destroy() {
    this.destroyed = true;
    this.publicRequest?.abort();
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('error', this.handleMediaError, true);
    for (const timer of this.copyTimers.values()) globalThis.clearTimeout(timer);
    this.copyTimers.clear();
  }

  hasWelcomeFallback() {
    return Boolean(this.root.querySelector('[data-my-offers-welcome-fallback]'));
  }

  hydrateCampaignEnd() {
    const label = this.root.querySelector('[data-my-offers-campaign-end]');
    const template = String(this.text.campaignEnds || '');
    const formatted = formatCampaignEnd(label?.dataset.campaignEndsAt, this.config.locale);
    if (!label || !formatted || !template.includes(CAMPAIGN_END_DATE_TOKEN)) return;
    label.textContent = template.split(CAMPAIGN_END_DATE_TOKEN).join(formatted);
  }

  publicElements() {
    const query = (selector) => this.root.querySelector(selector);
    return {
      state: query('[data-my-offers-public-state]'),
      loading: query('[data-my-offers-public-loading]'),
      error: query('[data-my-offers-public-error]'),
      message: query('[data-my-offers-public-message]'),
    };
  }

  showPublicState(state, message = '') {
    const elements = this.publicElements();
    if (!elements.state) return;
    elements.state.hidden = state === 'ready';
    if (elements.loading) elements.loading.hidden = state !== 'loading';
    if (elements.error) elements.error.hidden = state !== 'error';
    if (message && elements.message) elements.message.textContent = message;
  }

  async loadPublicOffers() {
    const snapshotUrl = String(this.config.snapshotUrl || '').trim();
    if (!snapshotUrl) {
      this.showPublicState(this.hasWelcomeFallback() ? 'ready' : 'error', this.text.snapshotError);
      return;
    }

    this.publicRequest?.abort();
    const controller = new AbortController();
    this.publicRequest = controller;
    this.showPublicState(this.hasWelcomeFallback() ? 'ready' : 'loading');
    for (const card of this.root.querySelectorAll('[data-my-offers-public-card]')) card.remove();
    this.sortVisibleCards();

    const timeout = globalThis.setTimeout(() => controller.abort(), PUBLIC_TIMEOUT_MS);
    try {
      const response = await fetch(snapshotUrl, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('snapshot_http_error');
      const snapshot = validatePublicOfferSnapshot(await response.json());
      const welcomeCode = normalizeOfferCode(this.config.welcomeDiscountCode);
      const offers = snapshot.items
        .filter((offer) => offerIsVisible(offer, new Date()))
        .filter((offer) => !welcomeCode || normalizeOfferCode(offer.code) !== welcomeCode);
      const anchor = this.root.querySelector('[data-my-offers-public-state]');
      const feed = this.root.querySelector('[data-my-offers-feed]');
      if (!anchor || !feed) throw new Error('snapshot_mount_missing');

      for (const offer of offers) feed.insertBefore(this.buildPublicCard(offer), anchor);
      this.showPublicState('ready');
      this.sortVisibleCards();
    } catch (error) {
      if (this.destroyed) return;
      this.showPublicState(this.hasWelcomeFallback() ? 'ready' : 'error', this.text.snapshotError);
    } finally {
      globalThis.clearTimeout(timeout);
      if (this.publicRequest === controller) this.publicRequest = null;
    }
  }

  buildPublicCard(offer) {
    const card = element('article', 'my-offer-card');
    card.dataset.myOfferCard = '';
    card.dataset.myOffersPublicCard = '';
    card.dataset.offerKey = offer.key;
    card.dataset.offerKind = offer.kind;
    card.dataset.offerPriority = String(offer.priority);
    card.dataset.offerStartsAt = offer.startsAt || '';
    card.dataset.offerEndsAt = offer.endsAt || '';
    if (offer.code) card.dataset.offerCode = offer.code;

    const content = element('div', 'my-offer-card__content');
    const badgeText = offer.kind === 'automatic_discount'
      ? this.text.automaticBadge
      : this.text.publicBadge;
    content.append(element('span', 'my-offer-card__badge', badgeText));
    if (offer.valueLabel) content.append(element('div', 'my-offer-card__value', offer.valueLabel));
    content.append(element('h3', '', offer.title));
    if (this.text.offerTerms) content.append(element('p', '', this.text.offerTerms));

    if (offer.code) {
      const code = element('code', 'my-offer-card__code', offer.code);
      code.dataset.myOffersCode = '';
      content.append(code);
    } else {
      content.append(element('span', 'my-offer-card__automatic', this.text.automatic));
    }

    const actions = element('div', 'my-offer-card__actions');
    if (offer.code) {
      const copy = button('my-offers-button my-offers-button--copy', this.text.copy);
      copy.dataset.myOffersCopy = offer.code;
      actions.append(copy);
    }
    const destination = safeOfferUrl(offer.ctaUrl, this.config.storefrontOrigin);
    if (destination) actions.append(this.createActionLink(destination, this.text.shopNow));

    const live = element('span', 'visually-hidden');
    live.dataset.myOffersCopyLive = '';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');

    card.append(content, actions, live);
    return card;
  }

  localizedInternalUrl(value) {
    if (value === '/collections/all' && this.config.allCollectionUrl) return this.config.allCollectionUrl;
    const root = String(this.config.localeRoot || '/').replace(/\/$/, '');
    if (!root || root === '/' || value === root || value.startsWith(`${root}/`)) return value;
    return `${root}${value}`;
  }

  createActionLink(destination, label) {
    const link = element('a', 'my-offers-button my-offers-button--primary', label);
    if (destination.startsWith('/')) {
      link.href = this.localizedInternalUrl(destination);
    } else {
      link.href = destination;
      link.target = '_blank';
      link.rel = 'noopener';
    }
    return link;
  }

  hydrateCampaignActions() {
    for (const mount of this.root.querySelectorAll('[data-my-offers-campaign-action]')) {
      const card = mount.closest('[data-my-offer-card]');
      const destination = safeOfferUrl(card?.dataset.campaignCtaUrl, this.config.storefrontOrigin);
      const label = String(card?.dataset.campaignCtaLabel || '').trim();
      if (!destination || !label || mount.firstElementChild) continue;
      mount.append(this.createActionLink(destination, label));
    }
  }

  sortVisibleCards() {
    const feed = this.root.querySelector('[data-my-offers-feed]');
    if (!feed) return;
    const records = [];
    for (const card of feed.querySelectorAll('[data-my-offer-card]')) {
      const record = {
        key: card.dataset.offerKey,
        kind: card.dataset.offerKind,
        code: card.dataset.offerCode || undefined,
        priority: parseNumber(card.dataset.offerPriority),
        startsAt: card.dataset.offerStartsAt || null,
        endsAt: card.dataset.offerEndsAt || null,
        element: card,
      };
      if (card.dataset.offerStatus === 'expired' || offerIsVisible(record, new Date())) records.push(record);
      else card.remove();
    }

    const ordered = dedupeOffers(sortOffers(records));
    const selected = new Set(ordered.map((record) => record.element));
    for (const record of records) {
      if (!selected.has(record.element)) record.element.remove();
    }
    for (const [index, record] of ordered.entries()) {
      record.element.hidden = index >= MAX_VISIBLE_OFFERS;
      feed.append(record.element);
    }
  }

  async copyCode(control) {
    const code = String(control.dataset.myOffersCopy || '').trim();
    if (!code) return;
    const card = control.closest('[data-my-offer-card]');
    const live = card?.querySelector('[data-my-offers-copy-live]');
    try {
      const copied = await writeClipboard(code);
      if (!copied) throw new Error('copy_failed');
      const previousTimer = this.copyTimers.get(control);
      if (previousTimer) globalThis.clearTimeout(previousTimer);
      control.classList.add('is-copied');
      control.textContent = `✓ ${this.text.copied}`;
      if (live) live.textContent = this.text.copySuccess;
      const timer = globalThis.setTimeout(() => {
        control.classList.remove('is-copied');
        control.textContent = this.text.copy;
        this.copyTimers.delete(control);
      }, COPY_FEEDBACK_DURATION_MS);
      this.copyTimers.set(control, timer);
    } catch {
      control.classList.remove('is-copied');
      control.textContent = this.text.copy;
      if (live) live.textContent = this.text.copyFailed;
    }
  }

  handleClick(event) {
    const copy = event.target.closest('[data-my-offers-copy]');
    if (copy && this.root.contains(copy)) {
      this.copyCode(copy);
      return;
    }
    const retry = event.target.closest('[data-my-offers-public-retry]');
    if (retry && this.root.contains(retry)) this.loadPublicOffers();
  }

  handleMediaError(event) {
    if (typeof HTMLImageElement === 'undefined' || !(event.target instanceof HTMLImageElement)) return;
    const media = event.target.closest('[data-my-offers-media], [data-my-offers-hero-media]');
    if (media) media.hidden = true;
  }

}

function mountWithin(scope = document) {
  const roots = scope.matches?.('[data-my-offers-root]')
    ? [scope]
    : scope.querySelectorAll('[data-my-offers-root]');
  for (const root of roots) {
    if (controllers.has(root)) continue;
    const controller = new MyOffersController(root);
    controllers.set(root, controller);
    controller.init();
  }
}

function unmountWithin(scope) {
  const root = scope.matches?.('[data-my-offers-root]')
    ? scope
    : scope.querySelector?.('[data-my-offers-root]');
  const controller = root ? controllers.get(root) : null;
  controller?.destroy();
  if (root) controllers.delete(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountWithin(document), { once: true });
} else {
  mountWithin(document);
}

document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
document.addEventListener('shopify:section:unload', (event) => unmountWithin(event.target));
